import { useState, useCallback, useRef } from 'react';
import type { JacKeyNudgeType } from '@/components/jackey/JacKeyNudge';

interface NudgeState {
  type: JacKeyNudgeType;
  id: string;
}

// Track shown nudges in session to prevent repeats
const shownNudgesThisSession = new Set<string>();

export function useJacKeyNudges() {
  const [activeNudge, setActiveNudge] = useState<NudgeState | null>(null);
  const nudgeQueue = useRef<JacKeyNudgeType[]>([]);

  // Show a nudge for a specific event
  const showNudge = useCallback((type: JacKeyNudgeType) => {
    // Prevent showing the same nudge type twice in a session
    if (shownNudgesThisSession.has(type)) {
      return;
    }

    // If there's already an active nudge, queue this one
    if (activeNudge) {
      if (!nudgeQueue.current.includes(type)) {
        nudgeQueue.current.push(type);
      }
      return;
    }

    shownNudgesThisSession.add(type);
    setActiveNudge({
      type,
      id: `${type}-${Date.now()}`,
    });
  }, [activeNudge]);

  // Dismiss the current nudge and show next in queue
  const dismissNudge = useCallback(() => {
    setActiveNudge(null);

    // Process queue after a brief delay
    setTimeout(() => {
      if (nudgeQueue.current.length > 0) {
        const nextType = nudgeQueue.current.shift();
        if (nextType && !shownNudgesThisSession.has(nextType)) {
          shownNudgesThisSession.add(nextType);
          setActiveNudge({
            type: nextType,
            id: `${nextType}-${Date.now()}`,
          });
        }
      }
    }, 500);
  }, []);

  // Map badge types to nudge types
  const showBadgeNudge = useCallback((badgeKey: string) => {
    const nudgeType = badgeKey as JacKeyNudgeType;
    
    // Only show nudges for supported badge types
    const supportedTypes: JacKeyNudgeType[] = [
      'first_expression',
      'relationship_celebrant',
      'birthday_champion',
      'friendship_keeper',
      'consistency_star',
      'love_ambassador',
      'legacy_builder',
    ];

    if (supportedTypes.includes(nudgeType)) {
      showNudge(nudgeType);
    }
  }, [showNudge]);

  return {
    activeNudge,
    showNudge,
    showBadgeNudge,
    dismissNudge,
  };
}
